import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";

import { services, type Transport } from "@tdelivr/api-client";

import { boundedNote, PER_FETCH, type BoardRow } from "../board.ts";
import {
  ACTIVATED_BY_OPERATOR,
  admissible,
  branchRefusal,
  FULFILMENT_MODES,
  LOCATION_CREATE_SCOPE,
  LOCATION_READ_SCOPE,
  MERCHANT_CREATE_SCOPE,
  MERCHANT_LIFECYCLE_SCOPE,
  MERCHANT_READ_SCOPE,
  merchantRow,
  statesPresent,
  type BranchDraft,
  type MerchantRecord,
} from "../merchants.ts";
import { stateForError, type ViewState } from "../view-state.ts";
import { RecordsBoard } from "./RecordsBoard.tsx";

/**
 * Merchants: the shops trading in this tenant, admitting a new one, and letting it trade.
 *
 * `src/merchants.ts` carries why this board exists, why the tenant administers merchants when
 * Document 01 §265 does not say so, and why it has no design. The short version: MER-01's create
 * was held by no client because no screen spent it, so **no merchant existed in any tenant** — and
 * the merchant portal's `merchant_id` claim had nothing to point at, so its six boards drew empty
 * states and the rider had nothing to shop from.
 *
 * ## The creation workflow, in the order an operator does it
 *
 * Three steps, and they are three because MER-01 models them as three rather than because a form
 * was split up:
 *
 *   1. **Admit the shop.** `POST /v1/merchants` takes a legal name, a trading name, a registration
 *      number and a contact address, and answers a merchant at `pending_review`. Nothing is
 *      trading yet, and that is MER-01's own initial state rather than a workflow invented here.
 *   2. **Let it trade.** `POST /v1/merchants/{id}/status` moves it to `active` with a reason and
 *      the version it was read at. Separate from the create on purpose: a shop is admitted by
 *      whoever takes the application and allowed to trade by whoever checked it, and MER-01 keeps
 *      the two apart so an audit log can show both.
 *   3. **Give it a branch.** A merchant with no location has nothing a rider can order from —
 *      MER-02 holds the address, the hours and the service it trades under. That step is **not on
 *      this board** and the footnote says so: `POST /v1/merchant-locations` needs fields this
 *      board does not collect, and a second form here would be guessing at a design.
 *
 * The board shows where a shop is in that sequence rather than narrating it: the state column is
 * MER-01's own status, and the branch count is MER-02's answer for that merchant.
 */

const merchants = services.commerce__merchant;
const locations = services.commerce__merchant_location;

export interface MerchantsProps {
  readonly transport: Transport;
  /**
   * The scopes this session carries.
   *
   * Read rather than assumed, because two of this board's three commands are new scopes and a
   * token minted before the realm was reconciled does not carry them. A button that fails is
   * worse than one that says which permission it is short of.
   */
  readonly scopes: ReadonlySet<string>;
  readonly crumbs?: readonly string[];
}

interface Loaded {
  readonly merchants: readonly MerchantRecord[];
  readonly truncated: boolean;
  /** Branch counts per merchant, or `"unread"` where MER-02 did not answer for one. */
  readonly branches: ReadonlyMap<string, number | "unread">;
}

interface Draft {
  readonly legalName: string;
  readonly tradingName: string;
  readonly registrationNumber: string;
  readonly contactEmail: string;
}

const EMPTY: Draft = { legalName: "", tradingName: "", registrationNumber: "", contactEmail: "" };

/**
 * A branch, pre-filled with the one value an operator should not have to look up.
 *
 * `Africa/Nairobi` is the deployment's own zone and the only field here with a sensible
 * default — a branch code names a shop's own branch, a name and an address are the shop's, and
 * a coordinate guessed by a console would put a pin in the wrong street. The rest start empty
 * on purpose.
 */
const EMPTY_BRANCH: BranchDraft = {
  branchCode: "",
  name: "",
  addressLabel: "",
  latitude: "",
  longitude: "",
  timeZone: "Africa/Nairobi",
  fulfillmentModes: "pickup",
  preparationMinutes: "15",
};

/** Which form the panel is showing, since this board collects two different records. */
type Mode = "closed" | "admit" | "branch";

export function Merchants({ transport, scopes, crumbs }: MerchantsProps): ReactNode {
  const [loaded, setLoaded] = useState<Loaded | undefined>(undefined);
  const [failure, setFailure] = useState<ViewState | undefined>(undefined);
  const [state, setState] = useState("");
  const [selected] = useState<string | undefined>(undefined);
  const [mode, setMode] = useState<Mode>("closed");
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [branch, setBranch] = useState<BranchDraft>(EMPTY_BRANCH);
  /** The shop a branch is being added to. Set when the branch form opens. */
  const [branchFor, setBranchFor] = useState<MerchantRecord | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const [formFailure, setFormFailure] = useState<string | undefined>(undefined);
  const [outcome, setOutcome] = useState<string | undefined>(undefined);

  const load = useCallback(async () => {
    const merchantClient = merchants.createMerchantClient(transport);
    const locationClient = locations.createMerchantLocationClient(transport);
    try {
      const answered = await merchantClient.listMerchants(undefined, String(PER_FETCH), undefined);

      /*
       * Settled, not all-or-nothing: one shop's branches failing must not cost the board every
       * other shop's row. The same rule the driver-onboarding board applies to DRV-03, and the
       * same reason — a screen that answers nothing because one source was unreachable is worse
       * than one that says which source it was.
       */
      const reads = await Promise.allSettled(
        answered.merchants.map(async (merchant) => {
          const page = await locationClient.listMerchantLocations(merchant.id, String(PER_FETCH));
          return [merchant.id, page.merchantLocations.length] as const;
        }),
      );

      const branches = new Map<string, number | "unread">();
      for (const [index, read] of reads.entries()) {
        const id = answered.merchants[index]?.id;
        if (id === undefined) continue;
        branches.set(id, read.status === "fulfilled" ? read.value[1] : "unread");
      }

      setLoaded({
        merchants: answered.merchants,
        truncated: typeof answered.nextCursor === "string" && answered.nextCursor.length > 0,
        branches,
      });
      setFailure(undefined);
    } catch (error) {
      setFailure(stateForError(error, [MERCHANT_READ_SCOPE, LOCATION_READ_SCOPE]));
      setLoaded(undefined);
    }
  }, [transport]);

  useEffect(() => {
    void load();
  }, [load]);

  const rows: readonly BoardRow[] | undefined = useMemo(() => {
    if (loaded === undefined) return undefined;
    const all = loaded.merchants.map((merchant) =>
      merchantRow(merchant, loaded.branches.get(merchant.id) ?? "unread"),
    );
    return state === "" ? all : all.filter((row) => row.state.label === state);
  }, [loaded, state]);

  const admit = useCallback(() => {
    setBusy(true);
    setFormFailure(undefined);
    merchants
      .createMerchantClient(transport)
      .createMerchant(
        {
          legalName: draft.legalName.trim(),
          tradingName: draft.tradingName.trim(),
          registrationNumber: draft.registrationNumber.trim(),
          contactEmail: draft.contactEmail.trim(),
        },
        {
          /*
           * Derived from the registration number rather than minted fresh, so a double submission
           * — the thing an operator does when a slow network makes them press again — replays
           * instead of admitting the same shop twice. A registration number is the one field here
           * that identifies a business rather than describing it.
           */
          idempotencyKey: `tenant-admin/admit/${draft.registrationNumber.trim()}`,
        },
      )
      .then((answer) => {
        setOutcome(
          answer.replayed
            ? `${answer.merchant.tradingName} was already admitted; nothing was created twice.`
            : `${answer.merchant.tradingName} is admitted and awaiting review.`,
        );
        setMode("closed");
        setDraft(EMPTY);
        return load();
      })
      .catch((error: unknown) => {
        setFormFailure(error instanceof Error ? error.message : "MER-01 refused the merchant.");
      })
      .finally(() => {
        setBusy(false);
      });
  }, [draft, transport, load]);

  const addBranch = useCallback(() => {
    if (branchFor === undefined) return;
    /*
     * Refused here before it is sent, because MER-02 answers one refusal for a body that breaks
     * several rules and an operator would otherwise fix them a round trip at a time.
     */
    const refusal = branchRefusal(branch);
    if (refusal !== undefined) {
      setFormFailure(refusal);
      return;
    }

    setBusy(true);
    setFormFailure(undefined);
    locations
      .createMerchantLocationClient(transport)
      .createMerchantLocation(
        {
          merchantId: branchFor.id,
          branchCode: branch.branchCode.trim(),
          name: branch.name.trim(),
          addressLabel: branch.addressLabel.trim(),
          coordinates: {
            latitude: Number(branch.latitude),
            longitude: Number(branch.longitude),
          },
          timeZone: branch.timeZone.trim(),
          fulfillmentModes: branch.fulfillmentModes.split(",").filter(Boolean) as (
            "pickup" | "merchant_delivery" | "courier_delivery"
          )[],
          preparationMinutes: Number(branch.preparationMinutes),
        },
        {
          /*
           * Derived from the shop and its branch code, which together name the branch. A second
           * press on a slow network adds the same branch rather than a duplicate of it.
           */
          idempotencyKey: `tenant-admin/branch/${branchFor.id}/${branch.branchCode.trim()}`,
        },
      )
      .then(() => {
        setOutcome(`${branch.name.trim()} is a branch of ${branchFor.tradingName}.`);
        setMode("closed");
        setBranch(EMPTY_BRANCH);
        setBranchFor(undefined);
        return load();
      })
      .catch((error: unknown) => {
        setFormFailure(error instanceof Error ? error.message : "MER-02 refused the branch.");
      })
      .finally(() => {
        setBusy(false);
      });
  }, [branch, branchFor, transport, load]);

  /**
   * The shop that most needs a branch: a trading one with none.
   *
   * A shop that is trading and has nowhere to be ordered from is invisible to every rider, and
   * it is the one state on this board an operator cannot see the consequence of — the row looks
   * finished. So the board offers the next step rather than waiting to be asked.
   */
  const branchless = useMemo(() => {
    if (loaded === undefined) return undefined;
    return loaded.merchants.find(
      (merchant) => merchant.status === "active" && loaded.branches.get(merchant.id) === 0,
    );
  }, [loaded]);

  /** The shop the activate command would act on, when the form is closed. */
  const target = useMemo(
    () => (loaded === undefined ? undefined : admissible(loaded.merchants, selected)),
    [loaded, selected],
  );

  const letTrade = useCallback(() => {
    if (target === undefined) return;
    setBusy(true);
    merchants
      .createMerchantClient(transport)
      .changeMerchantStatus(
        target.id,
        {
          status: "active",
          reason: ACTIVATED_BY_OPERATOR,
          /* The version the row was read at, so two administrators cannot both admit it. */
          expectedVersion: target.version,
        },
        { idempotencyKey: `tenant-admin/activate/${target.id}/${String(target.version)}` },
      )
      .then(() => {
        setOutcome(`${target.tradingName} is trading.`);
        return load();
      })
      .catch((error: unknown) => {
        setFailure(stateForError(error, [MERCHANT_LIFECYCLE_SCOPE]));
      })
      .finally(() => {
        setBusy(false);
      });
  }, [target, transport, load]);

  const bounded = boundedNote(
    loaded?.merchants.length,
    loaded?.truncated ?? false,
    "merchants",
    "newest first",
  );

  /* Why the primary cannot be pressed, where that is a fact rather than a permission. */
  const unavailable = !scopes.has(MERCHANT_CREATE_SCOPE)
    ? `This sign-in does not carry ${MERCHANT_CREATE_SCOPE}. Signing out and in again takes the grant the realm now holds.`
    : undefined;

  return (
    <RecordsBoard
      crumbs={crumbs ?? []}
      title="Merchant onboarding"
      source="MER-01 · MER-02"
      description="The shops trading in your account, and the ones waiting to."
      columns={["Shop", "State", "Branches"]}
      rows={rows}
      {...(failure === undefined ? {} : { failure })}
      onRetry={() => {
        void load();
      }}
      purpose="A shop appears here as soon as it is admitted, before it is allowed to trade."
      primary={{
        label: "Admit a merchant",
        onPress: () => {
          setOutcome(undefined);
          setMode("admit");
        },
        busy: busy && mode === "admit",
        ...(unavailable === undefined ? {} : { unavailable }),
      }}
      /*
       * One panel, two records. A board that admits a shop and gives it a branch is collecting two
       * different things, and the alternative — a second board for branches — would separate a
       * shop from the thing that makes it visible, which is the step most likely to be forgotten.
       */
      {...(mode === "closed"
        ? {}
        : mode === "admit"
          ? {
              form: {
                open: true,
                title: "Admit a merchant",
                // The page's own rail glyph, not a new one: neither of this board's two
                // forms is in the design's FORMS table. Q-288.
                icon: "storefront",
                legend: "Merchant",
                fields: [
                  {
                    name: "tradingName",
                    label: "Trading name",
                    value: draft.tradingName,
                    onChange: (value: string): void => {
                      setDraft((current) => ({ ...current, tradingName: value }));
                    },
                    hint: "What riders will see. The shop's own name for itself.",
                  },
                  {
                    name: "legalName",
                    label: "Legal name",
                    value: draft.legalName,
                    onChange: (value: string): void => {
                      setDraft((current) => ({ ...current, legalName: value }));
                    },
                    hint: "The registered entity, which is often not the trading name.",
                  },
                  {
                    name: "registrationNumber",
                    label: "Registration number",
                    value: draft.registrationNumber,
                    onChange: (value: string): void => {
                      setDraft((current) => ({ ...current, registrationNumber: value }));
                    },
                    hint: "Used to recognise a repeated submission, so admitting twice admits once.",
                  },
                  {
                    name: "contactEmail",
                    label: "Contact address",
                    type: "email" as const,
                    value: draft.contactEmail,
                    onChange: (value: string): void => {
                      setDraft((current) => ({ ...current, contactEmail: value }));
                    },
                    hint: "Where the shop is written to. MER-01 holds it; nothing sends to it yet.",
                  },
                ],
                submitLabel: "Admit",
                onSubmit: admit,
                onCancel: (): void => {
                  setMode("closed");
                  setFormFailure(undefined);
                },
                busy,
                ...(formFailure === undefined ? {} : { failure: formFailure }),
              },
            }
          : {
              form: {
                open: true,
                title: `Add a branch of ${branchFor?.tradingName ?? "this shop"}`,
                icon: "storefront",
                legend: "Branch",
                fields: [
                  {
                    name: "name",
                    label: "Branch name",
                    value: branch.name,
                    onChange: (value: string): void => {
                      setBranch((current) => ({ ...current, name: value }));
                    },
                    hint: "What a rider sees beside the shop, such as Westlands.",
                  },
                  {
                    name: "branchCode",
                    label: "Branch code",
                    value: branch.branchCode,
                    onChange: (value: string): void => {
                      setBranch((current) => ({ ...current, branchCode: value }));
                    },
                    hint: "The shop's own code for it. Letters, digits, hyphens and underscores.",
                  },
                  {
                    name: "addressLabel",
                    label: "Address",
                    value: branch.addressLabel,
                    onChange: (value: string): void => {
                      setBranch((current) => ({ ...current, addressLabel: value }));
                    },
                    hint: "Written as a courier would read it.",
                  },
                  {
                    name: "latitude",
                    label: "Latitude",
                    type: "number" as const,
                    min: -90,
                    max: 90,
                    step: "any",
                    value: branch.latitude,
                    onChange: (value: string): void => {
                      setBranch((current) => ({ ...current, latitude: value }));
                    },
                    hint: "Where the pin goes. A wrong one sends couriers to the wrong street.",
                  },
                  {
                    name: "longitude",
                    label: "Longitude",
                    type: "number" as const,
                    min: -180,
                    max: 180,
                    step: "any",
                    value: branch.longitude,
                    onChange: (value: string): void => {
                      setBranch((current) => ({ ...current, longitude: value }));
                    },
                  },
                  {
                    name: "timeZone",
                    label: "Time zone",
                    value: branch.timeZone,
                    onChange: (value: string): void => {
                      setBranch((current) => ({ ...current, timeZone: value }));
                    },
                    hint: "Opening hours are read in this zone, not the operator's.",
                  },
                  {
                    name: "fulfillmentModes",
                    label: "Can be ordered by",
                    type: "modes" as const,
                    choices: [...FULFILMENT_MODES],
                    value: branch.fulfillmentModes,
                    onChange: (value: string): void => {
                      setBranch((current) => ({ ...current, fulfillmentModes: value }));
                    },
                    hint: "At least one. These are the three MER-02 publishes.",
                  },
                  {
                    name: "preparationMinutes",
                    label: "Preparation minutes",
                    type: "number" as const,
                    min: 0,
                    max: 1440,
                    value: branch.preparationMinutes,
                    onChange: (value: string): void => {
                      setBranch((current) => ({ ...current, preparationMinutes: value }));
                    },
                    hint: "How long this branch takes to have an order ready.",
                  },
                ],
                submitLabel: "Add branch",
                onSubmit: addBranch,
                onCancel: (): void => {
                  setMode("closed");
                  setBranchFor(undefined);
                  setFormFailure(undefined);
                },
                busy,
                ...(formFailure === undefined ? {} : { failure: formFailure }),
              },
            })}
      filter={{
        unfiltered: "All states",
        options: (rows === undefined ? [] : statesPresent(rows)).map((label) => ({
          value: label,
          label,
        })),
        value: state,
        onChange: setState,
      }}
      note={{
        kind: "info",
        glyph: "info",
        text:
          outcome ??
          (target !== undefined
            ? `${target.tradingName} is awaiting review. Letting it trade is the next step.`
            : branchless !== undefined
              ? `${branchless.tradingName} is trading and has no branch, so no rider can see it. Adding one is the next step.`
              : "A shop admitted here is awaiting review and cannot trade until it is let through."),
      }}
      {...(bounded === undefined ? {} : { bounded })}
      footnote="No design file draws this board — the design draws a merchant's own portal and a rider's list of shops, and nothing that admits a shop to a tenant. So its words and layout are this console's own conventions and it sits outside the screen ratio, as the analytics boards and driver onboarding do. Admitting a shop leaves it at MER-01's own pending_review; letting it trade is a second, separate command so that an audit log shows who took the application and who checked it. Giving a shop its first branch is MER-02's POST /v1/merchant-locations, and it is on this board: the same panel collects it, because a board that admitted a shop and left it invisible would separate a shop from the thing that makes it orderable. Until a shop has a branch, a rider has nothing to order from it, which is why the board offers that step unprompted for a trading shop with none. A branch's coordinates are typed rather than picked from a map: no map control is designed for this console, and a pin a console guessed would send couriers to the wrong street."
      exportName="merchants"
      detailSubtitle="Merchants · MER-01 · MER-02"
      detailNote="A shop is admitted first and allowed to trade second, so an audit log shows both."
      /*
       * The next step, whatever it is. A shop goes admitted → trading → has a branch, and the
       * board offers whichever of those is outstanding rather than making an operator know the
       * sequence. Letting a shop trade comes first because a shop that cannot trade has no use
       * for a branch.
       *
       * A trading shop with no branch is the state worth volunteering: its row looks finished and
       * it is invisible to every rider, which is the one consequence on this board an operator
       * cannot see.
       */
      {...(target !== undefined && scopes.has(MERCHANT_LIFECYCLE_SCOPE)
        ? { secondary: { label: `Let ${target.tradingName} trade`, onPress: letTrade, busy } }
        : branchless !== undefined && scopes.has(LOCATION_CREATE_SCOPE)
          ? {
              secondary: {
                label: `Add a branch of ${branchless.tradingName}`,
                onPress: (): void => {
                  setOutcome(undefined);
                  setFormFailure(undefined);
                  setBranchFor(branchless);
                  setMode("branch");
                },
                busy,
              },
            }
          : {})}
    />
  );
}
